"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import Button from "@/shared/ui/components/button";
import { type CheckoutSession } from "@/types";
import { useCheckoutSessionV3 } from "@/modules/checkout-session";
import { logInfo, logError } from "@/services/logging/logger";
import CircularProgress from "@mui/material/CircularProgress";
import * as threeDSLib from "@/lib/3ds";
import { browserAgent } from "@/shared/telemetry/newrelic";
import { newLocalStorageStore } from "@/lib/store";
import { withCheckoutId } from "@/lib/checkout-id";

interface ThreeDSRedirectPageProps {
  onCheckoutFetched: (session: CheckoutSession) => void;
}

const COUNTDOWN_SECONDS = 3;

/**
 * ThreeDSRedirectPage - Interstitial page for 3DS verification
 *
 * This page serves as a transition step before redirecting the user to their bank
 * for 3DS verification. It addresses the following UX concerns:
 *
 * 1. Provides clear communication about what's happening (redirecting to bank)
 * 2. Shows a countdown timer before auto-redirect (3 seconds)
 * 3. Offers a manual "Proceed to Bank" button for immediate action
 * 4. Prevents redirect loops using localStorage flags
 *
 * Flow:
 * 1. User arrives here after payment submission when 3DS is required
 * 2. Redirect URL is retrieved from session's paymentResult.redirectUrl
 * 3. After 3 seconds (or manual click), user is redirected to bank
 * 4. Bank redirects back to /3ds/success or /3ds/failed
 * 5. localStorage flag prevents re-redirection on return
 */
export function ThreeDSRedirectPage(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _props: ThreeDSRedirectPageProps,
) {
  // Note: props.onCheckoutFetched is intentionally unused - this page handles its own
  // navigation and calling it would trigger the redirect handler to navigate away
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: session } = useCheckoutSessionV3();
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasRedirectedRef = useRef(false);

  const sessionId = session?.id ?? "";

  // Get the redirect URL from sessionStorage first (set by payment-confirmation)
  // Fall back to session's paymentResult.redirectUrl if not in sessionStorage
  const redirectUrl = (() => {
    if (!sessionId) return "";
    try {
      const storedUrl = sessionStorage.getItem(
        `cosmos_3ds_redirect_${sessionId}`,
      );
      if (storedUrl) return storedUrl;
    } catch {
      // sessionStorage might be blocked
    }
    return session?.paymentResult?.redirectUrl ?? "";
  })();

  // Check if 3DS has already been completed (prevent loop)
  const check3dsAlreadyCompleted = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;

    const store = newLocalStorageStore(window, sessionId);

    // Check both the completion flag and the result
    const isCompleted = await threeDSLib.is3dsCompleted(store);
    const result = await threeDSLib.get3dsResult(store);

    return isCompleted || result === threeDSLib.Result.OK;
  }, [sessionId]);

  // Perform the redirect to the bank
  const performRedirect = useCallback(async () => {
    // Guard against multiple redirects
    if (hasRedirectedRef.current || isRedirecting) {
      return;
    }

    // Check if 3DS was already completed
    const alreadyCompleted = await check3dsAlreadyCompleted();
    if (alreadyCompleted) {
      logInfo(
        "threeds-redirect",
        "3DS already completed, navigating to confirmation",
        {
          sessionId,
        },
      );
      // Clean up sessionStorage
      try {
        sessionStorage.removeItem(`cosmos_3ds_redirect_${sessionId}`);
      } catch {
        // Ignore
      }
      navigate(withCheckoutId("/confirmation"), { replace: true });
      return;
    }

    if (!redirectUrl) {
      logError(
        "threeds-redirect",
        "No redirect URL available",
        new Error("Missing redirectUrl"),
        { sessionId },
      );
      // If no redirect URL, go back to confirmation page
      navigate(withCheckoutId("/confirmation"), { replace: true });
      return;
    }

    hasRedirectedRef.current = true;
    setIsRedirecting(true);

    // Clear the countdown interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    logInfo("threeds-redirect", "Redirecting to bank for 3DS verification", {
      sessionId,
      redirectUrl: redirectUrl.substring(0, 50) + "...", // Log partial URL for security
    });

    // CRITICAL: Set the redirect initiated flag BEFORE redirecting to bank
    // This flag is used to detect return from bank (regardless of return URL)
    // Without this, frictionless flows that return to any URL (not /3ds/success)
    // would cause infinite redirect loops
    const store = newLocalStorageStore(window, sessionId);
    await threeDSLib.set3dsRedirectInitiated(store);
    threeDSLib.mark3dsCheckoutSession(sessionId);

    browserAgent.addPageAction("THREEDS_REDIRECT_TO_BANK", {
      checkout_id: sessionId,
      redirect_url_domain: (() => {
        try { return new URL(redirectUrl).hostname; } catch { return "unknown"; }
      })(),
    });

    // Clean up sessionStorage before redirecting
    try {
      sessionStorage.removeItem(`cosmos_3ds_redirect_${sessionId}`);
    } catch {
      // Ignore
    }

    // Perform the redirect
    window.location.href = redirectUrl;
  }, [
    redirectUrl,
    sessionId,
    isRedirecting,
    check3dsAlreadyCompleted,
    navigate,
  ]);

  // Handle manual button click
  const handleManualRedirect = useCallback(() => {
    logInfo("threeds-redirect", "Manual redirect button clicked", {
      sessionId,
      remainingSeconds: countdown,
    });
    performRedirect();
  }, [performRedirect, sessionId, countdown]);

  // Check on mount if 3DS was already completed (loop prevention)
  useEffect(() => {
    const checkIfAlreadyCompleted = async () => {
      // Only check if 3DS was already completed - this prevents redirect loops
      const alreadyCompleted = await check3dsAlreadyCompleted();
      if (alreadyCompleted) {
        logInfo(
          "threeds-redirect",
          "3DS already completed on mount, skipping to confirmation",
          {
            sessionId,
          },
        );
        navigate(withCheckoutId("/confirmation"), { replace: true });
        return;
      }
    };

    if (sessionId) {
      checkIfAlreadyCompleted();
    }
  }, [sessionId, check3dsAlreadyCompleted, navigate]);

  // Handle case where there's no redirect URL after session is loaded
  // This gives the SWR cache time to update before making a decision
  useEffect(() => {
    // Don't check until session is loaded
    if (!session) return;

    // If there's a redirect URL, we're good - let the countdown handle it
    if (redirectUrl) return;

    // No redirect URL - wait 2 seconds before navigating away
    // This gives time for any cache updates to propagate
    const timeoutId = setTimeout(() => {
      logInfo(
        "threeds-redirect",
        "No redirect URL after waiting, navigating to confirmation",
        {
          sessionId,
          status: session.status,
        },
      );
      navigate(withCheckoutId("/confirmation"), { replace: true });
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [session, redirectUrl, sessionId, navigate]);

  // Countdown timer effect
  useEffect(() => {
    // Don't start countdown if already redirecting or no redirect URL
    if (isRedirecting || !redirectUrl || !session) {
      return;
    }

    logInfo("threeds-redirect", "Starting countdown", {
      sessionId,
      seconds: COUNTDOWN_SECONDS,
    });

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Time's up! Perform redirect
          performRedirect();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isRedirecting, redirectUrl, session, sessionId, performRedirect]);

  // If session is loading, show loading state
  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <CircularProgress size={40} style={{ color: "#9600F1" }} />
      </div>
    );
  }

  return (
    <AppWrapper
      visible={true}
      showLanguageSwitcher={false}
      showCartButton={false}
      headerBottomSpacing="none"
      disableHeaderInteractions={true}
      stickyFooter={() => (
        <Button
          size="large"
          onClick={handleManualRedirect}
          loading={isRedirecting}
          disabled={isRedirecting}
        >
          {isRedirecting
            ? t("threeds.redirecting")
            : t("threeds.proceed_to_bank")}
        </Button>
      )}
    >
      {() => (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 py-10">
          {/* Bank Icon/Illustration */}
          <div className="w-24 h-24 mb-8 rounded-full bg-gradient-to-br from-purple-100 to-purple-200 flex items-center justify-center">
            <svg
              className="w-12 h-12 text-purple-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
          </div>

          {/* Main Message */}
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-3">
            {t("threeds.title")}
          </h1>

          <p className="text-gray-600 text-center text-lg mb-8 max-w-xs">
            {t("threeds.redirecting_message")}
          </p>

          {/* Countdown Display */}
          {!isRedirecting && countdown > 0 && (
            <div className="flex flex-col items-center mb-6">
              <div className="relative w-20 h-20 mb-4">
                {/* Circular progress background */}
                <svg
                  className="w-20 h-20 transform -rotate-90"
                  viewBox="0 0 36 36"
                >
                  <path
                    className="text-gray-200"
                    d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <path
                    className="text-purple-600 transition-all duration-1000 ease-linear"
                    d="M18 2.0845
                        a 15.9155 15.9155 0 0 1 0 31.831
                        a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray={`${(countdown / COUNTDOWN_SECONDS) * 100}, 100`}
                  />
                </svg>
                {/* Countdown number */}
                <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-purple-600">
                  {countdown}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {countdown === 1
                  ? t("threeds.auto_redirect", { seconds: countdown })
                  : t("threeds.auto_redirect_plural", { seconds: countdown })}
              </p>
            </div>
          )}

          {/* Redirecting state */}
          {isRedirecting && (
            <div className="flex flex-col items-center">
              <CircularProgress size={32} style={{ color: "#9600F1" }} />
              <p className="text-sm text-gray-500 mt-4">
                {t("threeds.taking_to_bank")}
              </p>
            </div>
          )}

          {/* Security badge */}
          <div className="flex items-center gap-2 mt-8 px-4 py-2 bg-green-50 rounded-full">
            <svg
              className="w-4 h-4 text-green-600"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-green-700 font-medium">
              {t("threeds.secure_badge")}
            </span>
          </div>

          {/* Info text */}
          <p className="text-xs text-gray-400 text-center mt-6 max-w-xs">
            {t("threeds.otp_info")}
          </p>
        </div>
      )}
    </AppWrapper>
  );
}
